import {
  TestCaseAutomationFramework,
  SaveFullTestCaseRequest,
  TestCaseAutomationStatusKey,
  TestCasePriority,
  TestStep,
  TestStepDraft,
} from '../../../models/test-case.model';
import { stepConditionsOf } from '../../../shared/data-sheet-filters.codec';
import { isLocalId } from '../test-step-table/test-step-local.util';

export interface TestCaseFormRaw {
  title: string;
  description: string;
  testGoal: string;
  preconditions: string;
  notes: string;
  type: string;
  testType: string;
  priority: TestCasePriority;
  status: string;
  assigneeUserId: string;
  tags: string[];
  automationStatus: TestCaseAutomationStatusKey;
  automationFramework: TestCaseAutomationFramework | null;
  automationLocator: string;
}

export interface TestCaseFormLoadedFreetext {
  description: string;
  testGoal: string;
  preconditions: string;
}

export interface TestCaseFormOptionalIds {
  requirementId: string;
  storyJiraKey: string;
  bugJiraKey: string;
}

export function loadedFreetextOf(values: TestCaseFormLoadedFreetext): TestCaseFormLoadedFreetext {
  return { description: values.description, testGoal: values.testGoal, preconditions: values.preconditions };
}

function nullable(value: string): string | null {
  return value || null;
}

function clearable(current: string, loaded: string | undefined): string | null {
  if (current.trim()) return current;
  return loaded?.trim() ? '' : null;
}

function trimmedNullable(value: string): { value: string | null; cleared: boolean } {
  const trimmed = value.trim();
  return { value: trimmed || null, cleared: !trimmed };
}

function toTestStepDraft(step: TestStep): TestStepDraft {
  const isLocal = isLocalId(step.id);
  return {
    id: isLocal ? null : step.id,
    clientId: isLocal ? step.id : null,
    // NEX-546: Nur NEUE (lokale) Zeilen tragen die Definition-UUID als Verknuepfung. Persistierte
    // Zeilen identifiziert das BE ueber `id` (id + testStepId zusammen -> 400). Neu getippte
    // Schritte haben keine Referenz (null).
    testStepId: isLocal ? (step.libraryStepId ?? null) : null,
    kind: step.kind,
    title: step.title ?? null,
    action: step.action ?? null,
    expectedResult: step.expectedResult ?? null,
    notes: step.notes ?? null,
    tcdl: step.tcdl ?? null,
    status: null,
    calledModuleId: step.calledModuleId ?? null,
    stepRole: step.stepRole ?? null,
    category: step.category ?? null,
    assigneeUserId: step.assigneeUserId ?? null,
    tags: step.tags ?? [],
    dataSheetId: step.dataSheetId ?? null,
    dataSheetFilters: step.dataSheetFilters ?? null,
    // Immer die ganze Liste: Das Backend nimmt sie vor `stepCondition`, `[]` entfernt alle.
    stepConditions: step.kind === 'MODULE_REF' ? stepConditionsOf(step) : null,
  };
}

export function toSaveFullRequest(
  raw: TestCaseFormRaw,
  ids: TestCaseFormOptionalIds,
  steps: TestStep[],
  loaded: TestCaseFormLoadedFreetext | null = null,
): SaveFullTestCaseRequest {
  return {
    title: raw.title,
    description: clearable(raw.description, loaded?.description),
    testGoal: clearable(raw.testGoal, loaded?.testGoal),
    preconditions: clearable(raw.preconditions, loaded?.preconditions),
    requirementId: nullable(ids.requirementId),
    storyJiraKey: nullable(ids.storyJiraKey),
    bugJiraKey: nullable(ids.bugJiraKey),
    notes: nullable(raw.notes),
    type: raw.type,
    testType: raw.testType,
    priority: raw.priority,
    status: raw.status,
    assigneeUserId: trimmedNullable(raw.assigneeUserId).value,
    tags: raw.tags,
    automationStatus: raw.automationStatus,
    automationFramework: raw.automationFramework,
    automationLocator: trimmedNullable(raw.automationLocator).value,
    steps: steps.map(toTestStepDraft),
    requirementCoverage: null,
  };
}
